import { Unit } from '../../types/units';
import { Stairwell } from '../../types/stairwells';
import { LevelModel, RoomModel } from '../../types/protocols';
import { roomLevelKeys } from '../../Helpers/roomUtils';
import { naturalCompare } from '../../Helpers/naturalSort';
import { sortedStairwells } from '../../Helpers/stairwellUtils';
import { unitRoomKeys } from '../../Helpers/unitUtils';

/** A group of the units list: one per trappuppgång, then the units without one. */
export interface UnitSection {
    /** Stairwell key, or 'none'. */
    key: string;
    /** Null when the building has no stairwells: the list is one unnamed group. */
    title: string | null;
    units: Unit[];
}

/**
 * The units list grouped by trappuppgång in natural name order, then "Utan
 * trappuppgång" (no key, or a key whose stairwell is gone). Units keep their
 * order. Empty groups are left out; without stairwells everything is one
 * untitled group.
 */
export const unitSections = (units: Unit[], stairwells: { [key: string]: Stairwell }): UnitSection[] => {
    const ordered = sortedStairwells(stairwells);
    if (ordered.length === 0) return units.length > 0 ? [{ key: 'none', title: null, units }] : [];
    const sections: UnitSection[] = ordered.map(stairwell => ({
        key: stairwell.key,
        title: stairwell.name,
        units: units.filter(unit => unit.stairwellKey === stairwell.key),
    }));
    sections.push({
        key: 'none',
        title: 'Utan trappuppgång',
        units: units.filter(unit => !(unit.stairwellKey && stairwells[unit.stairwellKey])),
    });
    return sections.filter(section => section.units.length > 0);
};

/** One level of a unit in the drawing pane: its current drawing, if any, and the unit's rooms on it. */
export interface UnitLevelPanel {
    level: LevelModel;
    /** The level's current drawing key, when the drawing exists. */
    blueprintKey?: string;
    /** The unit's rooms on this level, a room on several levels on each; natural order by number or name. */
    rooms: RoomModel[];
}

const roomSortName = (room: RoomModel): string => room.number || room.label || '';

/**
 * The levels the unit's rooms are on, highest first, each with those rooms.
 * Rooms whose level no longer exists, and dangling room keys, are skipped.
 */
export const unitLevelPanels = (
    unit: Pick<Unit, 'rooms'>,
    levels: { [key: string]: LevelModel },
    rooms: { [key: string]: RoomModel }
): UnitLevelPanel[] => {
    const byLevel = new Map<string, RoomModel[]>();
    for (const roomKey of unitRoomKeys(unit)) {
        const room = rooms[roomKey];
        if (!room) continue;
        for (const levelKey of roomLevelKeys(room, levels)) {
            byLevel.set(levelKey, [...(byLevel.get(levelKey) ?? []), room]);
        }
    }
    return Array.from(byLevel.entries())
        .map(([levelKey, levelRooms]) => {
            const level = levels[levelKey];
            const blueprintKey = level.currentBlueprintKey && level.blueprints?.[level.currentBlueprintKey]
                ? level.currentBlueprintKey
                : undefined;
            return {
                level,
                blueprintKey,
                rooms: [...levelRooms].sort((a, b) => naturalCompare(roomSortName(a), roomSortName(b)) || a.key.localeCompare(b.key)),
            };
        })
        .sort((a, b) => b.level.number - a.level.number || a.level.key.localeCompare(b.level.key));
};
